import React from "react";

// A derived figure being ASSEMBLED, one line at a time.
//
// Eleven of the fifteen figures on a shield were not cast, and until you have
// watched one being made it is very hard to believe that. So this draws the
// making: four rows, each showing the line or lines that were read, and the
// line they came to. A row arrives when its line lands on the shield above, so
// the two are one movement rather than two things happening near each other.
//
// TWO OPERATIONS, DRAWN DIFFERENTLY, because they are different.
//   transpose — a Daughter is the Mothers read DOWN. Each of its lines is one
//               Mother's line, moved. Nothing is combined and nothing is lost,
//               so the row is an arrow: this line, over there.
//   add       — every other figure is two figures added line by line. The row
//               is an arithmetic sentence, and it shows the count as well as
//               the shortcut: "alike makes two" is the consequence, the rule
//               is the parity of the total.
//
// Nothing here is computed in the browser. `addition` comes off the payload,
// where Ruby worked it out and spec/services/geomancy/derivation_spec.rb
// proved it — the arithmetic is the one thing on this page a reader is being
// asked to trust, and it is not going to be re-derived in a view.
const dots = (count) => "·".repeat(count);

export default function Assembly({ step, figures, landed, active }) {
  if (!step || step.kind === "cast") return null;

  const made = figures[step.made[0]];
  const sources = step.sources.map((source) => ({ ...source, figure: figures[source.figure] }));

  return (
    <figure className="geo-assembly" data-testid="geo-assembly" data-kind={step.kind}>
      <figcaption className="geo-assembly__caption">
        {step.kind === "transpose"
          ? `${made.printedName} is read off the four Mothers, one line from each.`
          : `${sources[0].note} added to ${sources[1].note}, line by line.`}
      </figcaption>

      <ol className="geo-assembly__rows">
        {[0, 1, 2, 3].map((line) => {
          const here = landed === null || line < landed;
          const now = line === active;
          const classes = ["geo-assembly__row"];
          if (!here && !now) classes.push("geo-assembly__row--waiting");
          if (now) classes.push("geo-assembly__row--active");

          return (
            <li key={line} className={classes.join(" ")} data-testid={`geo-assembly-row-${line}`}>
              <span className="geo-assembly__index">{line + 1}</span>

              {step.kind === "transpose" ? (
                <>
                  {/* A transposition reads one line from each of the four
                      Mothers, so there is a source per row. A row without one
                      renders the result alone rather than throwing: a figure
                      half-drawn is recoverable, a blank page is not. */}
                  <span className="geo-assembly__from">{sources[line]?.note ?? ""}</span>
                  <span className="geo-assembly__dots">
                    {sources[line] ? dots(sources[line].figure.lines[sources[line].line]) : ""}
                  </span>
                  <span className="geo-assembly__op" aria-hidden="true">
                    →
                  </span>
                </>
              ) : (
                <>
                  <span className="geo-assembly__dots">{dots(sources[0].figure.lines[line])}</span>
                  <span className="geo-assembly__op" aria-hidden="true">
                    +
                  </span>
                  <span className="geo-assembly__dots">{dots(sources[1].figure.lines[line])}</span>
                  <span className="geo-assembly__sum">
                    {step.addition[line].total}{" "}
                    <span className="geo-assembly__parity">
                      {step.addition[line].total % 2 === 0 ? "even" : "odd"}
                    </span>
                  </span>
                  <span className="geo-assembly__op" aria-hidden="true">
                    =
                  </span>
                </>
              )}

              <span className="geo-assembly__dots geo-assembly__dots--result">
                {dots(made.lines[line])}
              </span>
              <span className="geo-assembly__leaves">
                {made.lines[line] === 2 ? "two" : "one"}
              </span>
            </li>
          );
        })}
      </ol>

      {/* A transposition moves the same sixteen lines; an addition throws
          information away. Saying which is which is the difference between a
          reader who has seen a trick and one who understands the shield. */}
      <p className="geo-assembly__note">
        {step.kind === "transpose"
          ? "No new points. The Daughters are the Mothers' own lines, read down the columns instead of across the rows — the same sixteen lines, turned."
          : "Several different pairs give the same sum, so an addition loses information. That is why a shield narrows as it descends, and why the Judge is one figure rather than fifteen."}
      </p>
    </figure>
  );
}
